import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import cn from 'classnames'
import useBreakpoints from '../../hooks/useBreakpoints'
import Chevron from '../../../assets/media/select-arrow-black.svg?svgr-webpack'
import HashAnchor from 'js/components/HashAnchor/HashAnchor'
import { useInView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'
import { createRef } from 'react'
import { useEffect } from 'react'
import useScroll from 'js/hooks/useScroll'
import { useMemo } from 'react'

export function OnPageNav({ children }) {
  const { hash } = useLocation()
  const { LG, XXL } = useBreakpoints()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [navRef, navInView] = useInView({
    threshold: LG ? 0.15 : 0.05,
  })
  const refs = React.useRef([])
  const { y: yScroll } = useScroll()

  const linkData = useMemo(
    () =>
      React.Children.map(children, (child) => {
        const { id, heading } = child.props

        return { id, heading }
      }),
    [children],
  )

  const jumpTo = (id) => {
    if (document.getElementById(id)) {
      window.history.replaceState(null, null, `#${id}`)
      document.getElementById(id).scrollIntoView(true)

      const index = linkData.findIndex(({ id }) => id === id)
      setCurrentIndex(index)
    }
  }

  refs.current = children.map((_, i) => refs.current[i] ?? createRef())

  useEffect(() => {
    const indexFromHash = linkData.findIndex(({ id }) => id === hash.slice(1))
    if (indexFromHash > 0) setCurrentIndex(indexFromHash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const invertedRefArray = refs.current.slice().reverse()
    const invertedClosestIndex = invertedRefArray.findIndex(
      ({ current }) => current && current.getBoundingClientRect().top <= 50,
    )

    if (invertedClosestIndex < 0) return setCurrentIndex(0)

    setCurrentIndex(refs.current.length - 1 - invertedClosestIndex)
  }, [yScroll])

  return (
    <>
      {!navInView && (
        <div
          className={cn(
            'sticky top-0 z-20 bg-Bg py-12 text-center shadow-elevated transition lg:py-16 xl:py-24',
          )}
        >
          <DropdownNav
            currentIndex={currentIndex}
            linkData={linkData}
            onClick={jumpTo}
          />
        </div>
      )}
      <div ref={navRef}>
        <div className={cn('o-width-limiter py-36 text-center')}>
          <div className={cn('my-auto')}>
            <p
              className={cn('mr-6 text-TypeSub lg:mr-0 xl:mb-2 xxl:mb-0', {
                'type-nano': !LG,
                'type-small': LG && !XXL,
              })}
            >
              <FormattedMessage
                id="on_this_page"
                defaultMessage="On this page:"
              />
            </p>

            <HorizontalNav
              currentIndex={currentIndex}
              linkData={linkData}
              onClick={jumpTo}
            />
          </div>
        </div>
      </div>
      {React.Children.map(children, (child, index) => {
        return <div ref={refs.current[index]}>{child}</div>
      })}
    </>
  )
}

OnPageNav.propTypes = {
  children: PropTypes.node,
}

function HorizontalNav({ currentIndex, linkData, onClick }) {
  return (
    <nav>
      <ul
        className={cn(
          'type-small flex flex-wrap justify-center text-center font-demibold xxl:type-20',
        )}
      >
        {linkData.map(({ id, heading }, i) => {
          return (
            <li key={id} className={cn('mx-8 py-4 lg:ml-24 lg:mr-0 lg:py-0')}>
              <button
                className={cn(
                  'border-b-2 border-transparent pb-2 text-Type transition',
                  {
                    'border-Brand': currentIndex === i,
                    'hover:border-Border': currentIndex !== i,
                  },
                )}
                type="button"
                onClick={() => onClick(id)}
              >
                {heading}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

HorizontalNav.propTypes = {
  currentIndex: PropTypes.number,
  linkData: PropTypes.array,
  onClick: PropTypes.func,
}

function DropdownNav({ currentIndex, linkData, onClick }) {
  const { LG } = useBreakpoints()
  const [showMenu, setShowMenu] = useState(false)

  const currentSection = linkData[currentIndex]

  return (
    <div
      className="relative px-20 text-left"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-14 text-grey">
        <FormattedMessage id="on_this_page" defaultMessage="On this page:" />
      </span>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="type-upper-fat ml-8"
      >
        <span aria-hidden="true" className="flex">
          <span className="inline-block truncate pt-2 text-left">
            {currentSection.heading}
          </span>
          <Chevron className="relative my-auto ml-4" height={LG ? '5' : '4'} />
        </span>
        <span className="sr-only">Show menu</span>
      </button>
      {showMenu && (
        <ul
          className="absolute rounded-4 border-1 border-BorderSub bg-Bg py-12 text-left shadow-elevated"
          style={{ top: 'calc(100% + 5px)', width: LG ? 250 : 220 }}
        >
          {linkData.map(({ id, heading }) => {
            return (
              <li
                key={id}
                className={cn('type-small pb-4 pt-6', LG ? 'px-20' : 'px-12', {
                  'type-small-fat': id === currentSection.id,
                })}
              >
                <button
                  className={cn('text-Type')}
                  type="button"
                  onClick={() => {
                    setShowMenu(false)
                    onClick(id)
                  }}
                >
                  {heading}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

DropdownNav.propTypes = {
  currentIndex: PropTypes.number,
  linkData: PropTypes.array,
  onClick: PropTypes.func,
  onMenuClick: PropTypes.func,
  showMenu: PropTypes.bool,
}

export function OnPageNavSection({ children, id }) {
  return (
    <HashAnchor id={id}>
      <div>{children}</div>
    </HashAnchor>
  )
}

OnPageNavSection.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string,
}
